"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";

import { fadeVariants } from "@/lib/animations";
import { type FoodCategory, type FoodIcon, getRandomFoodIcon } from "@/lib/categorization/icons";
import { cn } from "@/lib/utils";

interface RandomFoodIconProps {
  categories?: FoodCategory[];
  className?: string;
  strokeWidth?: number;
}

/**
 * RandomFoodIcon - Displays a random food icon based on category
 *
 * @param categories - Array of food categories to choose from (defaults to all)
 * @param className - CSS classes for the icon (default: "size-20 sm:size-24 text-primary")
 * @param strokeWidth - Icon stroke width (default: 1.5)
 */
export function RandomFoodIcon({
  categories,
  className = "",
  strokeWidth = 1,
}: RandomFoodIconProps) {
  const [foodIcon, setFoodIcon] = useState<FoodIcon | null>(null);

  useEffect(() => {
    const icon = getRandomFoodIcon(categories);

    if (!icon) {
      console.warn("RandomFoodIcon: No icons available for selected categories");
      return;
    }

    setFoodIcon(icon);
  }, [categories]);

  return (
    <div className="relative size-20 sm:size-24 flex items-center justify-center">
      <AnimatePresence mode="wait">
        {foodIcon && (
          <motion.div
            key="food-icon"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={fadeVariants}
          >
            <foodIcon.icon
              className={cn("size-20 sm:size-24", className)}
              strokeWidth={strokeWidth}
              style={{ color: `var(--${foodIcon.colorClass})` }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
